import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../config/runtime';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

interface BusinessSubscription {
  id: string;
  businessId: string;
  businessName: string;
  subscriptionTypeId: string;
  subscriptionTypeCode: string;
  subscriptionTypeName: string;
  dateStart: string;
  dateEnd: string | null;
  isActive: boolean;
  status: string;
  cancelledDate: string | null;
  autoRenew: boolean;
  nextBillingDate: string | null;
  adminNotes: string | null;
}

interface BusinessOption { id: string; name: string; abn: string }
interface SubscriptionType { id: string; code: string; name: string; price: number }

const STATUS_OPTIONS = ['Pending', 'Active', 'Cancelled', 'Expired'];

const statusColor = (status: string): 'warning' | 'success' | 'default' | 'error' => {
  switch (status) {
    case 'Active': return 'success';
    case 'Pending': return 'warning';
    case 'Cancelled': return 'default';
    case 'Expired': return 'error';
    default: return 'default';
  }
};

const toDateInputValue = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

const BusinessSubscriptionsManagement: React.FC = () => {
  const auth = useAuth();
  const apiUrl = getApiBaseUrl();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [subscriptions, setSubscriptions] = useState<BusinessSubscription[]>([]);
  const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
  const [planTypes, setPlanTypes] = useState<SubscriptionType[]>([]);
  const [businessFilter, setBusinessFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<BusinessSubscription | null>(null);
  const [editSubscriptionTypeId, setEditSubscriptionTypeId] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editDateEnd, setEditDateEnd] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Extend dialog
  const [extendTarget, setExtendTarget] = useState<BusinessSubscription | null>(null);
  const [extendMode, setExtendMode] = useState<'days' | 'date'>('days');
  const [extendDays, setExtendDays] = useState('30');
  const [extendDate, setExtendDate] = useState('');
  const [extendNotes, setExtendNotes] = useState('');
  const [savingExtend, setSavingExtend] = useState(false);

  // Comp dialog
  const [compOpen, setCompOpen] = useState(false);
  const [compBusinessId, setCompBusinessId] = useState('');
  const [compSubscriptionTypeId, setCompSubscriptionTypeId] = useState('');
  const [compDateEnd, setCompDateEnd] = useState('');
  const [compNotes, setCompNotes] = useState('');
  const [savingComp, setSavingComp] = useState(false);

  const [dialogError, setDialogError] = useState<string | null>(null);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (businessFilter) params.set('businessId', businessFilter);
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`${apiUrl}/api/admin/business-subscriptions?${params.toString()}`, {
        headers: authHeader,
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      const data = await response.json();
      setSubscriptions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    const [bizRes, planRes] = await Promise.all([
      fetch(`${apiUrl}/api/businesses/list`, { headers: authHeader }),
      fetch(`${apiUrl}/api/subscription-types`, { headers: authHeader }),
    ]);
    if (bizRes.ok) setBusinesses(await bizRes.json());
    if (planRes.ok) setPlanTypes(await planRes.json());
  };

  useEffect(() => {
    if (auth.isSignedIn) {
      fetchLookups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn]);

  useEffect(() => {
    if (auth.isSignedIn) fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, businessFilter, statusFilter]);

  const openEdit = (sub: BusinessSubscription) => {
    setEditTarget(sub);
    setEditSubscriptionTypeId(sub.subscriptionTypeId);
    setEditStatus(sub.status);
    setEditDateEnd(toDateInputValue(sub.dateEnd));
    setEditNotes(sub.adminNotes ?? '');
    setDialogError(null);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    setDialogError(null);
    try {
      const response = await fetch(`${apiUrl}/api/admin/business-subscriptions/${editTarget.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionTypeId: editSubscriptionTypeId,
          status: editStatus,
          dateEnd: editDateEnd ? new Date(editDateEnd).toISOString() : undefined,
          adminNotes: editNotes || undefined,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save subscription');
      }
      setEditTarget(null);
      await fetchSubscriptions();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingEdit(false);
    }
  };

  const openExtend = (sub: BusinessSubscription) => {
    setExtendTarget(sub);
    setExtendMode('days');
    setExtendDays('30');
    setExtendDate('');
    setExtendNotes('');
    setDialogError(null);
  };

  const handleSaveExtend = async () => {
    if (!extendTarget) return;
    setSavingExtend(true);
    setDialogError(null);
    try {
      const body: Record<string, unknown> = { adminNotes: extendNotes || undefined };
      if (extendMode === 'days') {
        body.additionalDays = parseInt(extendDays, 10);
      } else {
        body.newDateEnd = extendDate ? new Date(extendDate).toISOString() : undefined;
      }
      const response = await fetch(`${apiUrl}/api/admin/business-subscriptions/${extendTarget.id}/extend`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to extend subscription');
      }
      setExtendTarget(null);
      await fetchSubscriptions();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingExtend(false);
    }
  };

  const openComp = () => {
    setCompBusinessId('');
    setCompSubscriptionTypeId('');
    setCompDateEnd('');
    setCompNotes('');
    setDialogError(null);
    setCompOpen(true);
  };

  const handleSaveComp = async () => {
    if (!compBusinessId || !compSubscriptionTypeId) return;
    setSavingComp(true);
    setDialogError(null);
    try {
      const response = await fetch(`${apiUrl}/api/admin/businesses/${compBusinessId}/subscriptions/comp`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionTypeId: compSubscriptionTypeId,
          dateEnd: compDateEnd ? new Date(compDateEnd).toISOString() : undefined,
          adminNotes: compNotes || undefined,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to comp subscription');
      }
      setCompOpen(false);
      await fetchSubscriptions();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingComp(false);
    }
  };

  if (loading && subscriptions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Business Subscriptions</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
            <InputLabel>Business</InputLabel>
            <Select value={businessFilter} label="Business" onChange={(e) => setBusinessFilter(e.target.value)}>
              <MenuItem value="">All Businesses</MenuItem>
              {businesses.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, flex: { xs: '1 1 110px', sm: '0 0 auto' } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openComp}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Comp Subscription
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {subscriptions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: businessFilter ? 2 : 0 }}>
            {businessFilter
              ? `${businesses.find((b) => b.id === businessFilter)?.name ?? 'This business'} doesn't have a subscription yet.`
              : 'No subscriptions found'}
          </Typography>
          {businessFilter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCompBusinessId(businessFilter);
                setCompSubscriptionTypeId('');
                setCompDateEnd('');
                setCompNotes('');
                setDialogError(null);
                setCompOpen(true);
              }}
            >
              Assign a Subscription
            </Button>
          )}
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {subscriptions.map((sub) => (
            <Card key={sub.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{sub.businessName}</Typography>
                  <Chip label={sub.status} size="small" color={statusColor(sub.status)} />
                </Box>
                <Typography variant="body2" color="text.secondary">{sub.subscriptionTypeName}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(sub.dateStart).toLocaleDateString()} — {sub.dateEnd ? new Date(sub.dateEnd).toLocaleDateString() : 'Ongoing'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{sub.autoRenew ? 'Auto-renews' : 'No auto-renew'}</Typography>
                </Box>
                {sub.adminNotes && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {sub.adminNotes}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => openEdit(sub)}>Edit</Button>
                <Button size="small" onClick={() => openExtend(sub)}>Extend</Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Auto-Renew</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell>{sub.businessName}</TableCell>
                  <TableCell>{sub.subscriptionTypeName}</TableCell>
                  <TableCell><Chip label={sub.status} size="small" color={statusColor(sub.status)} /></TableCell>
                  <TableCell>{new Date(sub.dateStart).toLocaleDateString()}</TableCell>
                  <TableCell>{sub.dateEnd ? new Date(sub.dateEnd).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{sub.autoRenew ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{sub.adminNotes || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(sub)} sx={{ mr: 1 }}>Edit</Button>
                    <Button size="small" onClick={() => openExtend(sub)}>Extend</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subscription — {editTarget?.businessName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select value={editSubscriptionTypeId} label="Plan" onChange={(e) => setEditSubscriptionTypeId(e.target.value)}>
                {planTypes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={editStatus} label="Status" onChange={(e) => setEditStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="End Date"
              type="date"
              value={editDateEnd}
              onChange={(e) => setEditDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Admin Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={!!extendTarget} onClose={() => setExtendTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Subscription — {extendTarget?.businessName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <RadioGroup row value={extendMode} onChange={(e) => setExtendMode(e.target.value as 'days' | 'date')}>
              <FormControlLabel value="days" control={<Radio />} label="Additional Days" />
              <FormControlLabel value="date" control={<Radio />} label="New End Date" />
            </RadioGroup>
            {extendMode === 'days' ? (
              <TextField
                label="Additional Days"
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
              />
            ) : (
              <TextField
                label="New End Date"
                type="date"
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            )}
            <TextField
              label="Admin Notes"
              value={extendNotes}
              onChange={(e) => setExtendNotes(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveExtend}
            disabled={savingExtend || (extendMode === 'days' ? !extendDays : !extendDate)}
          >
            {savingExtend ? 'Saving…' : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comp dialog */}
      <Dialog open={compOpen} onClose={() => setCompOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comp a Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <FormControl fullWidth>
              <InputLabel>Business</InputLabel>
              <Select value={compBusinessId} label="Business" onChange={(e) => setCompBusinessId(e.target.value)}>
                {businesses.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select value={compSubscriptionTypeId} label="Plan" onChange={(e) => setCompSubscriptionTypeId(e.target.value)}>
                {planTypes.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="End Date (optional)"
              type="date"
              value={compDateEnd}
              onChange={(e) => setCompDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for a rolling/non-expiring comp"
              fullWidth
            />
            <TextField
              label="Admin Notes"
              value={compNotes}
              onChange={(e) => setCompNotes(e.target.value)}
              multiline
              minRows={2}
              placeholder="Reason for comping this subscription"
              inputProps={{ maxLength: 2000 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveComp}
            disabled={savingComp || !compBusinessId || !compSubscriptionTypeId}
          >
            {savingComp ? 'Saving…' : 'Comp Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessSubscriptionsManagement;
